import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Sparkles, Heart, HandHeart, Sprout, Users, Handshake, Dumbbell, MessageCircle, Loader2, Send } from 'lucide-react';
import BottomNav from '../components/BottomNav';
import CircleCommentSheet from '../components/CircleCommentSheet';
import NotificationBell from '../components/NotificationBell';
import { useAuth } from '../context/AuthContext';
import { IdentityToggle } from '../components/IdentityToggle';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Five branded reactions replace the old three — matches the app's supportive
// (never competitive) voice. Order matters: it's how they render on each card.
const REACTION_META = {
  love:    { icon: Heart,     colorOn: '#E76F51', label: 'Love This'       },
  needed:  { icon: HandHeart, colorOn: '#D6AD60', label: 'I Needed This'  },
  strong:  { icon: Dumbbell,  colorOn: '#AA8347', label: 'Stay Strong'    },
  growing: { icon: Sprout,    colorOn: '#79A87F', label: 'Growing'         },
  support: { icon: Users,     colorOn: '#5B8DEF', label: 'Sending Support' },
};

const timeAgo = (iso) => {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  const s = Math.max(0, (Date.now() - then) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

const initialsFromName = (n) => (n || '?').split(' ').map(p => p[0]).join('').slice(0, 2).toUpperCase();

const CommunityPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [today, setToday]           = useState(null);
  const [reflections, setReflections] = useState([]);
  const [me, setMe]                 = useState({ challenges: [], circles: [] });
  const [draft, setDraft]           = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading]       = useState(true);
  const [encouragements, setEncouragements] = useState([]);
  const [openEncourageId, setOpenEncourageId] = useState(null);
  const [postAnonymously, setPostAnonymously] = useState(false);
  const [featured, setFeatured] = useState(null);
  const [circleFilter, setCircleFilter] = useState('all'); // 'all' | circle id
  const [postCircle, setPostCircle] = useState('');
  // When set, opens the threaded comment sheet for a given reflection id.
  // Used both for the pinned daily-question and any feed reflection.
  const [openComments, setOpenComments] = useState(null);

  const authHeaders = useCallback(() => {
    const t = localStorage.getItem('rbr_token');
    return t ? { Authorization: `Bearer ${t}` } : {};
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = circleFilter && circleFilter !== 'all' ? `?limit=20&circle=${encodeURIComponent(circleFilter)}` : '?limit=20';
      const [tRes, rRes, mRes, eRes, fRes] = await Promise.all([
        fetch(`${API_URL}/api/community/today`),
        fetch(`${API_URL}/api/community/reflections${qs}`),
        user ? fetch(`${API_URL}/api/community/me`, { headers: authHeaders() }) : Promise.resolve(null),
        fetch(`${API_URL}/api/community/encouragements`),
        fetch(`${API_URL}/api/community/featured`),
      ]);
      setToday(await tRes.json());
      const rBody = await rRes.json();
      setReflections(rBody.reflections || []);
      if (mRes && mRes.ok) setMe(await mRes.json());
      const eBody = await eRes.json();
      setEncouragements(eBody.messages || []);
      const fBody = await fRes.json();
      setFeatured(fBody.reflection || null);
    } catch {
      toast.error('Could not load community — please try again');
    } finally {
      setLoading(false);
    }
  }, [user, authHeaders, circleFilter]);

  useEffect(() => { load(); }, [load]);

  const shareReflection = async () => {
    const text = draft.trim();
    if (!text) return;
    if (!user) { toast('Sign in to share your reflection'); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/community/reflections`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ content: text, anonymous: postAnonymously, circle: postCircle || null }),
      });
      const body = await res.json();
      if (!res.ok) {
        toast.error(body.detail || 'Could not share — try again');
      } else {
        setReflections((prev) => [body.reflection, ...prev]);
        setDraft('');
        setPostAnonymously(false);
        setPostCircle('');
        toast.success('Shared with the community 🌱');
      }
    } catch {
      toast.error('Network error — try again');
    } finally {
      setSubmitting(false);
    }
  };

  const react = async (id, reaction) => {
    if (!user) { toast('Sign in to encourage others'); return; }
    // Optimistic bump — we only know the count changed, not who reacted.
    try {
      const res = await fetch(`${API_URL}/api/community/reflections/${id}/react`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ reaction }),
      });
      const body = await res.json();
      if (res.ok) {
        setReflections((prev) => prev.map((r) => r.id === id ? body.reflection : r));
      }
    } catch { /* silently ignore — user can retry */ }
  };

  const toggleChallenge = async (id) => {
    if (!user) { toast('Sign in to join the challenge'); return; }
    try {
      const res = await fetch(`${API_URL}/api/community/challenges/${id}/toggle`, {
        method: 'POST', headers: authHeaders(),
      });
      const body = await res.json();
      if (res.ok) {
        setToday((prev) => ({
          ...prev,
          challenges: (prev?.challenges || []).map((c) => c.id === id ? { ...c, participants: body.participants } : c),
        }));
        setMe((prev) => ({
          ...prev,
          challenges: body.joined ? [...(prev.challenges || []), id] : (prev.challenges || []).filter((x) => x !== id),
        }));
        toast.success(body.joined ? 'Joined — one day at a time 🔥' : 'Left the challenge');
      }
    } catch { /* ignore */ }
  };

  const toggleCircle = async (id) => {
    if (!user) { toast('Sign in to join a circle'); return; }
    try {
      const res = await fetch(`${API_URL}/api/community/circles/${id}/toggle`, {
        method: 'POST', headers: authHeaders(),
      });
      const body = await res.json();
      if (res.ok) {
        setMe((prev) => ({
          ...prev,
          circles: body.joined ? [...(prev.circles || []), id] : (prev.circles || []).filter((x) => x !== id),
        }));
      }
    } catch { /* ignore */ }
  };

  const sendEncouragement = async (reflectionId, message) => {
    if (!user) { toast('Sign in to encourage others'); return; }
    setOpenEncourageId(null);
    // Optimistic bump — the count updates immediately even before the
    // request completes so the tap feels alive.
    setReflections((prev) => prev.map((r) => r.id === reflectionId
      ? { ...r, commentCount: (r.commentCount || 0) + 1 }
      : r));
    try {
      const res = await fetch(`${API_URL}/api/community/reflections/${reflectionId}/encourage`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ message }),
      });
      if (res.ok) {
        toast.success('Encouragement sent 💛');
      } else {
        // Roll back the optimistic bump if the server rejected it.
        setReflections((prev) => prev.map((r) => r.id === reflectionId
          ? { ...r, commentCount: Math.max(0, (r.commentCount || 1) - 1) }
          : r));
      }
    } catch {
      setReflections((prev) => prev.map((r) => r.id === reflectionId
        ? { ...r, commentCount: Math.max(0, (r.commentCount || 1) - 1) }
        : r));
    }
  };

  const challenge = today?.challenges?.[0];
  const inChallenge = challenge ? (me.challenges || []).includes(challenge.id) : false;

  return (
    <div className="min-h-screen pb-24" style={{ background: 'linear-gradient(180deg, #1c3a33 0%, #0f1f1c 100%)' }}>
      {/* Header */}
      <div className="px-5 pt-6 pb-4 flex items-start justify-between gap-3">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.28em]" style={{ color: '#D6AD60' }}>
            Community
          </p>
          <h1
            className="text-3xl mt-1 text-[#F8F6F0]"
            style={{ fontFamily: "'Playfair Display', serif", fontWeight: 600 }}
          >
            You don&rsquo;t grow alone.
          </h1>
        </div>
        <NotificationBell tone="dark" />
      </div>

      {/* Today's Reflection prompt */}
      <div className="px-5 mb-6">
        <div
          className="rounded-3xl p-5 shadow-2xl"
          style={{
            background: 'linear-gradient(135deg, #173F35 0%, #245549 100%)',
            border: '1px solid rgba(214,173,96,0.35)',
          }}
          data-testid="community-todays-reflection"
        >
          <p className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.24em] font-semibold mb-2" style={{ color: '#D6AD60' }}>
            <Sparkles className="w-3 h-3" />
            Today&rsquo;s Reflection
          </p>
          <p
            className="text-[18px] leading-snug text-[#F8F6F0] mb-4"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            &ldquo;{today?.prompt || 'Loading today\u2019s prompt…'}&rdquo;
          </p>
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Share what came up for you…"
            rows={2}
            maxLength={500}
            className="w-full px-3.5 py-2.5 rounded-2xl text-[13px] leading-relaxed resize-none bg-white/95 text-stone-800 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-[#D6AD60]/50"
            data-testid="community-reflection-input"
          />
          {/* Optional circle tag — helps returning users see reflections
              from the same season of life. */}
          <select
            value={postCircle}
            onChange={(e) => setPostCircle(e.target.value)}
            className="w-full mt-2 px-3 py-2 rounded-xl text-[12px] bg-white/95 text-stone-700 focus:outline-none focus:ring-2 focus:ring-[#D6AD60]/50"
            data-testid="community-post-circle"
          >
            <option value="">Post to everyone</option>
            {(today?.circles || []).map((c) => (
              <option key={c.id} value={c.id}>{`Tag → ${c.emoji} ${c.title}`}</option>
            ))}
          </select>
          <div className="mt-2 flex justify-start"><IdentityToggle anonymous={postAnonymously} onChange={setPostAnonymously} name={user?.displayName || user?.name} dark testId="community-anonymous-toggle" /></div>
          <div className="flex items-center justify-between mt-2 gap-2">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <span className="text-[10px] text-white/50 flex-shrink-0">{draft.length}/500</span>
            </div>
            <button
              type="button"
              onClick={shareReflection}
              disabled={!draft.trim() || submitting}
              className="rbr-btn-gold px-5 py-2 text-[12px] flex-shrink-0"
              data-testid="community-share-btn"
            >
              {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
              Share My Reflection
            </button>
          </div>
          {/* Comment on the daily question directly — the composer above
              creates a top-level post; this thread lets people riff on
              the question itself so conversations stay clustered under the
              day's prompt. */}
          {today?.pinnedId && (
            <button
              type="button"
              onClick={() => setOpenComments(today.pinnedId)}
              data-testid="community-question-thread-btn"
              className="w-full mt-3 inline-flex items-center justify-between gap-2 px-4 py-2.5 rounded-2xl transition-all active:scale-[0.99]"
              style={{ background: '#FFFCF3', border: '1px solid rgba(214,173,96,0.35)' }}
            >
              <span className="inline-flex items-center gap-2 text-[12.5px] font-semibold text-stone-800">
                <MessageCircle className="w-4 h-4" style={{ color: '#D6AD60' }} />
                {today.pinned?.commentCount > 0
                  ? `Comments — ${today.pinned.commentCount} ${today.pinned.commentCount === 1 ? 'reply' : 'replies'}`
                  : 'Comment on the question'}
              </span>
              <span className="text-[10px] uppercase tracking-wide text-stone-400">Open</span>
            </button>
          )}
        </div>
      </div>

      {/* Featured Reflection — auto-curated: the most-encouraged non-anonymous
          reflection from the last 48h. Gives contributors a reason to open
          up publicly. */}
      {featured && (
        <div className="px-5 mb-6" data-testid="community-featured">
          <p className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.24em] font-semibold mb-2" style={{ color: '#D6AD60' }}>
            <Sparkles className="w-3 h-3" /> Reflection of the Day
          </p>
          <article
            className="rounded-2xl p-5"
            style={{
              background: 'linear-gradient(135deg, rgba(214,173,96,0.14) 0%, rgba(23,63,53,0.30) 100%)',
              border: '1px solid rgba(214,173,96,0.45)',
            }}
          >
            <div className="flex items-center gap-3 mb-2">
              {featured.avatar ? (
                <img src={featured.avatar} alt="" className="w-8 h-8 rounded-full object-cover ring-1 ring-[#D6AD60]/60" />
              ) : (
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center text-[10px] font-semibold text-[#242824]"
                  style={{ background: 'linear-gradient(135deg,#D6AD60 0%,#AA8347 100%)' }}
                >
                  {(featured.name || '?').split(' ').map(p => p[0]).join('').slice(0,2).toUpperCase()}
                </div>
              )}
              <p className="text-[13px] font-semibold text-[#F8F6F0]">{featured.name}</p>
            </div>
            <p
              className="text-[15px] leading-relaxed text-[#F8F6F0]"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              &ldquo;{featured.content}&rdquo;
            </p>
          </article>
        </div>
      )}

      {/* Challenge banner */}
      {challenge && (
        <div className="px-5 mb-6" data-testid="community-challenge">
          <button
            type="button"
            onClick={() => toggleChallenge(challenge.id)}
            className="w-full rounded-2xl p-4 flex items-center gap-3 text-left active:scale-[0.99] transition-all"
            style={{
              background: inChallenge
                ? 'linear-gradient(135deg, rgba(214,173,96,0.35) 0%, rgba(170,131,71,0.30) 100%)'
                : 'rgba(248,246,240,0.06)',
              border: `1px solid ${inChallenge ? 'rgba(214,173,96,0.75)' : 'rgba(248,246,240,0.15)'}`,
            }}
          >
            <div className="w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0 text-xl"
                 style={{ background: 'rgba(255,180,80,0.20)' }}>
              {challenge.emoji}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[13px] font-semibold text-[#F8F6F0]">{challenge.title}</p>
              <p className="text-[11px] text-white/65">
                {challenge.participants.toLocaleString()} participating · {challenge.description}
              </p>
            </div>
            <span
              className="text-[10px] font-semibold uppercase tracking-wide px-2.5 py-1 rounded-full"
              style={{
                background: inChallenge ? '#D6AD60' : 'rgba(248,246,240,0.15)',
                color: inChallenge ? '#242824' : '#F8F6F0',
              }}
            >
              {inChallenge ? 'Joined' : 'Join'}
            </span>
          </button>
        </div>
      )}

      {/* Reflections feed */}
      <div className="px-5 mb-6">
        <div className="flex items-center justify-between mb-3 gap-2">
          <p className="text-[10px] uppercase tracking-[0.24em] font-semibold" style={{ color: '#D6AD60' }}>
            Community Reflections
          </p>
        </div>
        {/* Circle filter — horizontal chip row. Chips now navigate to the
            dedicated circle page (/circles/:id) so users get the full
            circle experience — today's exercise/question/discussion,
            circle-scoped feed, and threaded conversations. */}
        <div className="flex gap-1.5 overflow-x-auto no-scrollbar pb-2 mb-3" data-testid="community-circle-filter">
          <button
            type="button"
            onClick={() => setCircleFilter('all')}
            className={`text-[11px] font-semibold uppercase tracking-wide px-3 py-1.5 rounded-full flex-shrink-0 transition-all active:scale-95 ${circleFilter === 'all' ? 'bg-[#D6AD60] text-[#242824]' : 'bg-white/10 text-white/80 border border-white/25'}`}
            data-testid="community-filter-all"
          >
            All
          </button>
          {(today?.circles || []).map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => navigate(`/circles/${c.id}`)}
              data-testid={`community-filter-${c.id}`}
              className="text-[11px] font-medium px-3 py-1.5 rounded-full flex-shrink-0 inline-flex items-center gap-1 transition-all active:scale-95 bg-white/10 text-white/80 border border-white/25 hover:bg-white/20"
            >
              <span>{c.emoji}</span>{c.title}
            </button>
          ))}
        </div>
        {loading ? (
          <div className="text-center py-10 text-white/60 text-sm flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> loading…
          </div>
        ) : reflections.length === 0 ? (
          <div className="text-center py-10 text-white/60 text-sm">
            Nobody&rsquo;s shared yet today. Be the first to open the circle.
          </div>
        ) : (
          <div className="space-y-3">
            {reflections.map((r) => (
              <article
                key={r.id}
                className="rounded-2xl p-4"
                style={{ background: '#FFFCF3', border: '1px solid rgba(214,173,96,0.20)' }}
                data-testid={`community-reflection-${r.id}`}
              >
                <div className="flex items-center gap-3 mb-2">
                  {r.avatar ? (
                    <img src={r.avatar} alt="" className="w-8 h-8 rounded-full object-cover" />
                  ) : (
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center text-[10px] font-semibold text-[#242824]"
                      style={{ background: 'linear-gradient(135deg,#D6AD60 0%,#AA8347 100%)' }}
                    >
                      {initialsFromName(r.name)}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-[13px] font-semibold text-stone-800 truncate">{r.name}</p>
                    <p className="text-[10px] text-stone-400">{timeAgo(r.createdAt)}</p>
                  </div>
                </div>
                <p
                  className="text-[14px] leading-relaxed text-stone-700 mb-3"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  &ldquo;{r.content}&rdquo;
                </p>
                <div className="flex items-center gap-1 flex-wrap">
                  {Object.keys(REACTION_META).map((rk) => {
                    const meta = REACTION_META[rk];
                    const Icon = meta.icon;
                    const count = r.reactions?.[rk] || 0;
                    return (
                      <button
                        key={rk}
                        type="button"
                        onClick={() => react(r.id, rk)}
                        data-testid={`community-react-${r.id}-${rk}`}
                        aria-label={meta.label}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-medium bg-stone-50 hover:bg-stone-100 text-stone-600 border border-stone-200 transition-colors active:scale-95"
                        style={{ WebkitTapHighlightColor: 'transparent' }}
                        title={meta.label}
                      >
                        <Icon className="w-3 h-3" style={{ color: count > 0 ? meta.colorOn : undefined }} />
                        {count}
                      </button>
                    );
                  })}
                  <button
                    type="button"
                    onClick={() => setOpenComments(r.id)}
                    data-testid={`community-open-comments-${r.id}`}
                    aria-label="Open comments"
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-medium bg-stone-50 hover:bg-stone-100 text-stone-600 border border-stone-200 transition-colors active:scale-95"
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    <MessageCircle className="w-3 h-3" />
                    {r.commentCount || 0}
                    <span className="ml-0.5 text-[10px] uppercase tracking-wide text-stone-400">Reply</span>
                  </button>
                </div>

                {/* Send Encouragement — one-tap preset message so encouraging
                    someone takes zero effort. Toggles a small chip strip. */}
                <div className="mt-2 pt-2 border-t border-stone-100 relative">
                  {openEncourageId === r.id ? (
                    <div className="flex flex-wrap gap-1.5" data-testid={`community-encouragements-${r.id}`}>
                      {encouragements.map((m) => (
                        <button
                          key={m}
                          type="button"
                          onClick={() => sendEncouragement(r.id, m)}
                          className="text-[11px] font-medium px-2.5 py-1 rounded-full bg-[#F7F3EA] hover:bg-[#EAE1CC] text-[#5b4727] border border-[#D6AD60]/40 transition-colors active:scale-95"
                          data-testid={`community-encouragement-msg-${r.id}`}
                        >
                          {m}
                        </button>
                      ))}
                      <button
                        type="button"
                        onClick={() => setOpenEncourageId(null)}
                        className="text-[11px] font-medium px-2 py-1 rounded-full text-stone-400 hover:text-stone-600"
                      >
                        cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setOpenEncourageId(r.id)}
                      className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide px-3 py-1.5 rounded-full text-[#5b4727] border border-[#D6AD60]/40 bg-[#F7F3EA] hover:bg-[#EAE1CC] active:scale-95 transition-colors"
                      data-testid={`community-encourage-btn-${r.id}`}
                    >
                      <Send className="w-3 h-3" />
                      Send Encouragement
                    </button>
                  )}
                </div>
              </article>
            ))}
          </div>
        )}
      </div>

      {/* Circles */}
      <div className="px-5 mb-10">
        <p className="text-[10px] uppercase tracking-[0.24em] font-semibold mb-3" style={{ color: '#D6AD60' }}>
          Your Circles
        </p>
        <div className="flex flex-wrap gap-2">
          {(today?.circles || []).map((c) => {
            const joined = (me.circles || []).includes(c.id);
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => navigate(`/circles/${c.id}`)}
                data-testid={`community-circle-${c.id}`}
                className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-[12px] font-medium transition-all active:scale-95"
                style={{
                  background: joined ? '#D6AD60' : 'rgba(248,246,240,0.10)',
                  color: joined ? '#242824' : '#F8F6F0',
                  border: `1px solid ${joined ? '#D6AD60' : 'rgba(248,246,240,0.30)'}`,
                  WebkitTapHighlightColor: 'transparent',
                }}
              >
                <span>{c.emoji}</span>
                {c.title}
              </button>
            );
          })}
        </div>
        <p className="text-[11px] text-white/55 mt-4 leading-relaxed">
          Tap any circle to see today&rsquo;s exercise, question, or discussion — and share with the people in your season of life.
        </p>
      </div>

      <CircleCommentSheet
        reflectionId={openComments}
        open={!!openComments}
        onClose={() => setOpenComments(null)}
        onCommentAdded={() => {
          // Bump commentCount locally on whichever reflection was open
          // (pinned daily-question OR a feed entry) without a re-fetch.
          setToday((prev) => (prev && prev.pinned && prev.pinned.id === openComments
            ? { ...prev, pinned: { ...prev.pinned, commentCount: (prev.pinned.commentCount || 0) + 1 } }
            : prev));
          setReflections((prev) => prev.map((r) => (r.id === openComments
            ? { ...r, commentCount: (r.commentCount || 0) + 1 }
            : r)));
        }}
      />

      <BottomNav />
    </div>
  );
};

export default CommunityPage;
